import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ApiClientError } from '@/services/apiClient';
import { sportsVodApi, type ListLibraryQuery } from '@/services/sportsVodApi';
import {
  isKnownSportsVodSource,
  type SportsVodLibraryPage,
  type SportsVodSource,
  type SportsVodSportInfo,
  type SportsVodTitle,
} from '@/types/sportsVod';
import type { RootState } from '@/store';

export type SportsVodStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface SportsVodFilters {
  /** Multi-select sport ids ("" / empty = all). Order is preserved for URL shape. */
  sports: string[];
  /** Multi-select source names. Empty = all sources. */
  sources: SportsVodSource[];
  /** Free-text query, already trimmed. */
  q: string;
}

export interface SportsVodState {
  titles: SportsVodTitle[];
  sports: SportsVodSportInfo[];
  byId: Record<string, SportsVodTitle>;
  filters: SportsVodFilters;
  cursor: string | null;
  /** Whether the backend reports there's another page after `titles`. */
  hasMore: boolean;
  status: SportsVodStatus;
  /** True while `loadMore` is in-flight — keeps the first page visible. */
  loadingMore: boolean;
  error: string | null;
}

const initialState: SportsVodState = {
  titles: [],
  sports: [],
  byId: {},
  filters: { sports: [], sources: [], q: '' },
  cursor: null,
  hasMore: false,
  status: 'idle',
  loadingMore: false,
  error: null,
};

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

/**
 * The backend `/api/sports-vod/library` endpoint takes ONE `sport` and ONE
 * `source` (mirrors the live-sports filter shape). The slice carries arrays
 * so the UI can offer multi-select today; we pick the first entry for the
 * server query. Future iterations may switch to a CSV param + IN clause.
 */
function filtersToQuery(filters: SportsVodFilters): ListLibraryQuery {
  return {
    sport: filters.sports[0],
    source: filters.sources[0],
    q: filters.q,
  };
}

export const fetchSportsVodSports = createAsyncThunk<SportsVodSportInfo[], void, { rejectValue: string }>(
  'sportsVod/fetchSports',
  async (_arg, { rejectWithValue }) => {
    try {
      const { sports } = await sportsVodApi.listSports();
      return sports;
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const fetchSportsVodLibrary = createAsyncThunk<
  SportsVodLibraryPage,
  void,
  { rejectValue: string; state: { sportsVod: SportsVodState } }
>(
  'sportsVod/fetchLibrary',
  async (_arg, { getState, rejectWithValue }) => {
    try {
      const query = filtersToQuery(getState().sportsVod.filters);
      return await sportsVodApi.list(query);
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

export const loadMoreSportsVod = createAsyncThunk<
  SportsVodLibraryPage,
  void,
  { rejectValue: string; state: { sportsVod: SportsVodState } }
>(
  'sportsVod/loadMore',
  async (_arg, { getState, rejectWithValue }) => {
    try {
      const { filters, cursor } = getState().sportsVod;
      if (!cursor) return { titles: [], nextCursor: null };
      return await sportsVodApi.list({ ...filtersToQuery(filters), cursor });
    } catch (err) {
      return rejectWithValue(describeError(err));
    }
  },
);

const sportsVodSlice = createSlice({
  name: 'sportsVod',
  initialState,
  reducers: {
    setSportsFilter(state, action: PayloadAction<string[]>) {
      state.filters.sports = [...action.payload];
    },
    toggleSportFilter(state, action: PayloadAction<string>) {
      const id = action.payload;
      const idx = state.filters.sports.indexOf(id);
      if (idx >= 0) state.filters.sports.splice(idx, 1);
      else state.filters.sports.push(id);
    },
    setSourceFilter(state, action: PayloadAction<SportsVodSource[]>) {
      // Drop unknown sources defensively — the URL string is user-controlled.
      state.filters.sources = action.payload.filter(isKnownSportsVodSource);
    },
    toggleSourceFilter(state, action: PayloadAction<SportsVodSource>) {
      const id = action.payload;
      const idx = state.filters.sources.indexOf(id);
      if (idx >= 0) state.filters.sources.splice(idx, 1);
      else state.filters.sources.push(id);
    },
    setQueryFilter(state, action: PayloadAction<string>) {
      state.filters.q = action.payload;
    },
    resetFilters(state) {
      state.filters = { sports: [], sources: [], q: '' };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSportsVodSports.fulfilled, (state, action) => {
        state.sports = action.payload;
      })
      .addCase(fetchSportsVodLibrary.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchSportsVodLibrary.fulfilled, (state, action) => {
        state.status = 'ready';
        state.titles = action.payload.titles;
        state.cursor = action.payload.nextCursor;
        state.hasMore = action.payload.nextCursor !== null;
        const next: Record<string, SportsVodTitle> = {};
        for (const t of action.payload.titles) next[t.id] = t;
        state.byId = next;
      })
      .addCase(fetchSportsVodLibrary.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to load library';
      })
      .addCase(loadMoreSportsVod.pending, (state) => {
        state.loadingMore = true;
        state.error = null;
      })
      .addCase(loadMoreSportsVod.fulfilled, (state, action) => {
        state.loadingMore = false;
        // Append-and-dedupe by id; the backend cursor is forward-only but a
        // tab restoring from URL state may race with a refresh, so keep this
        // belt-and-suspenders.
        const seen = new Set(state.titles.map((t) => t.id));
        for (const t of action.payload.titles) {
          if (!seen.has(t.id)) {
            state.titles.push(t);
            state.byId[t.id] = t;
            seen.add(t.id);
          }
        }
        state.cursor = action.payload.nextCursor;
        state.hasMore = action.payload.nextCursor !== null;
      })
      .addCase(loadMoreSportsVod.rejected, (state, action) => {
        state.loadingMore = false;
        state.error = action.payload ?? action.error.message ?? 'Failed to load more';
      });
  },
});

export const {
  setSportsFilter,
  toggleSportFilter,
  setSourceFilter,
  toggleSourceFilter,
  setQueryFilter,
  resetFilters,
} = sportsVodSlice.actions;

export default sportsVodSlice.reducer;

export function selectSportsVodTitles(state: RootState): SportsVodTitle[] {
  return state.sportsVod.titles;
}

export function selectSportsVodFilters(state: RootState): SportsVodFilters {
  return state.sportsVod.filters;
}

export function selectSportsVodSports(state: RootState): SportsVodSportInfo[] {
  return state.sportsVod.sports;
}

export function selectSportsVodTitleById(state: RootState, id: string): SportsVodTitle | undefined {
  return state.sportsVod.byId[id];
}
